/**
 * descargasPosibles.js — v2
 *
 * Qué descargas se pueden hacer ahora mismo, y por qué no las demás.
 *
 * Vive aparte de `AccionesReunion.js` por una razón concreta: aquí está toda la
 * decisión y nada de pantalla, así que se puede probar sin montar React. La
 * decisión es la parte que importa —fue la que estuvo mal y dejó al usuario sin
 * poder descargar su acta— y la que más fácil se rompe al tocarla.
 *
 * La regla que gobierna todo esto: **ninguna opción se apaga en silencio**. Si
 * una descarga no se puede hacer, hay que poder decir por qué con palabras que
 * signifiquen algo para quien está mirando la pantalla, y cuando tenga arreglo,
 * cuál es.
 */

/** El texto de un informe HTML, con los saltos donde tocan. */
export function textoDeHtml(html) {
  return String(html || '')
    .replace(/<\/(p|h1|h2|h3|h4|li|tr|div)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * De dónde saca la IA los valores para rellenar el machote.
 *
 * Lo normal es la transcripción. Pero si no se tiene y sí hay un informe ya
 * generado, sirve igual: los datos están ahí. Sin esta segunda vía, abrir una
 * minuta ya guardada cuando la grabación no aparece dejaba el botón muerto sin
 * motivo real.
 *
 * El umbral de 40 caracteres descarta un HTML que solo trae un título o un
 * marcador vacío: mandar eso al modelo gasta tokens y devuelve un documento con
 * todos los campos en blanco.
 */
export function fuenteDeDatos(transcripcion, html) {
  if (typeof transcripcion === 'string' && transcripcion.trim()) return transcripcion;
  if (transcripcion && typeof transcripcion === 'object') return transcripcion;
  const texto = textoDeHtml(html);
  return texto.length > 40 ? texto : null;
}

/**
 * Lo que impide rellenar el machote de Word, si algo lo impide.
 *
 * Devuelve `null` cuando se puede. Cada traba lleva un `codigo` para poder
 * comprobarla, un `texto` para leer y, cuando tiene arreglo, el nombre del botón
 * que lleva a él.
 *
 * El orden es el de un diagnóstico y no es intercambiable: primero lo que falta
 * del todo (no hay formatos), luego lo que falta por decidir (ninguno elegido),
 * y solo al final lo que falta de la reunión. Al revés, quien no ha subido nada
 * leería «hace falta la transcripción», que es verdad pero no es su problema.
 */
export function trabaDelFormato({ formatos = [], formatoSel = '', fuente = null }) {
  if (!formatos || formatos.length === 0) {
    return {
      codigo: 'SIN_FORMATOS',
      texto: 'Todavía no has subido ningún formato de Word.',
      arreglo: 'Subir mi formato'
    };
  }

  const elegido = formatos.find(f => String(f.formatId) === String(formatoSel)) || null;
  if (!formatoSel || !elegido) {
    return { codigo: 'SIN_ELEGIR', texto: 'Elige arriba cuál de tus formatos quieres usar.' };
  }

  // Aquí había un "tu formato no tiene huecos marcados, márcalos con {{Campo}}".
  // Era pedirle al usuario que preparase su documento para la herramienta, y no
  // es lo que la gente tiene: lo que tienen guardado es el acta de la reunión
  // anterior, con sus tablas llenas de datos del mes pasado y ningún marcador.
  // Ahora el servidor deduce los huecos de la forma del documento, así que un
  // acta sin marcar se rellena igual y no hay nada que bloquear.

  if (!fuente) {
    return {
      codigo: 'SIN_FUENTE',
      texto: 'Hace falta la transcripción de la reunión, o un informe ya generado, para saber qué va en cada hueco.'
    };
  }

  return null;
}

/** Lo que impide exportar el informe generado, en Word o en PDF. */
export function trabaDelInforme(html) {
  return html
    ? null
    : { codigo: 'SIN_INFORME', texto: 'Genera primero el informe con «Generar informe».' };
}

/** Un nombre de archivo que Windows acepte. */
export function nombreLimpio(titulo) {
  const limpio = String(titulo || 'minuta')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  return limpio || 'minuta';
}

const descargasPosibles = {
  textoDeHtml, fuenteDeDatos, trabaDelFormato, trabaDelInforme, nombreLimpio
};
export default descargasPosibles;
